import { RouteConfig } from 'vue-router';
import { ModelType } from '@llws/platform-shared-types/types/db/auth';
import { IRouteConfigAuthMeta, IRouteMetaPermissionConfig, postTypes, ERouteMetaPermissionMode } from '@/shared';
import app from '@/main';

export const getBaseSiteAuth = () => ([{
  key: 'sites',
  keyType: 'site',
  accessType: 'read',
  mode: ERouteMetaPermissionMode.AT_LEAST_ONE,
}] as IRouteMetaPermissionConfig[]);

const auth: IRouteConfigAuthMeta = {
  required: true,
  permissions: getBaseSiteAuth(),
};

const siteSettingsAuth: IRouteConfigAuthMeta = {
  required: true,
  permissions: [{ keyType: ModelType.site, key: 'site_management', accessType: 'read', idParam: 'websiteId' }],
};

const superadminAuth: IRouteConfigAuthMeta = {
  required: true,
  permissions: ['superuser'],
};

const websites: RouteConfig = {
  path: '/websites',
  name: 'Websites',
  component: () => import('@/views/websites/Index.vue'),
  meta: {
    auth,
    title: 'Websites',
    titleKey: 'website.routes.websites',
    icon: 'globe',
    active: false,
    parent: null,
  },
};

const websiteSingle: RouteConfig = {
  path: '/websites/:websiteId/details',
  redirect: (vm) => {
    try {
      // check if the user has `pages` page permission, stay. Or app is not defined, stay.
      if (!app() || app()?.$rsGlobals.$userHasAccess('pages', 'read', `${vm.params.websiteId}`)) {
        return '/websites/:websiteId/pages';
      }
      if (app()?.$rsGlobals.$userHasAccess('cities', 'read', `${vm.params.websiteId}`)) {
        return '/websites/:websiteId/citypages';
      }
      if (app()?.$rsGlobals.$userHasAccess('site_management', 'read', `${vm.params.websiteId}`)) {
        return '/websites/:websiteId/global-content';
      }
      if (app()?.$rsGlobals.$userHasAccess('inquiries', 'read', `${vm.params.websiteId}`)) {
        return '/websites/:websiteId/form-submissions';
      }
      if (app()?.$rsGlobals.$userHasAccess('form_settings', 'read', `${vm.params.websiteId}`)) {
        return '/websites/:websiteId/forms/settings';
      }
      if (app()?.$rsGlobals.$userHasAccess('menu_editor', 'read', `${vm.params.websiteId}`)) {
        return '/websites/:websiteId/menu';
      }

      return '/websites/:websiteId/posts';
    } catch (e) {
      console.warn(e);
      return '/websites/:websiteId/pages';
    }
  },
  name: 'WebsiteSingle',
  component: () => import('@/views/websites/Index.vue'),
  meta: {
    auth,
    title: 'Website Details',
    titleKey: 'website.routes.websiteDetails',
    icon: 'globe',
    active: false,
    parent: websites,
  },
};

const pages: RouteConfig = {
  path: '/websites/:websiteId/pages',
  name: 'Pages',
  component: () => import('@/views/websites/single/pages/Index.vue'),
  meta: {
    auth: {
      ...auth,
      permissions: [{ keyType: 'site', key: 'pages', accessType: 'read', idParam: 'websiteId' }],
    },
    hidePageTitle: true,
    noPagePadding: true,
    hideFromNav: false,
    title: 'Pages',
    titleKey: 'website.routes.pages',
    icon: 'globe',
    active: false,
    parent: websiteSingle,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
    backButtonTarget: () => ({ isCustom: false, target: websites }),
  },
};

const posts: RouteConfig = {
  path: '/websites/:websiteId/posts',
  name: 'Posts',
  component: () => import('@/views/websites/single/posts/Index.vue'),
  meta: {
    // Base auth already requires at least one site read; keep it so list is visible for read-only users
    auth,
    hideFromNav: false,
    dynamicHideFromNav: () => !postTypes.some((pt) => app()?.$rsGlobals.$userHasAccess({
      keyType: 'site',
      key: pt.value.toLowerCase(),
      accessType: 'read',
      idParam: 'websiteId',
    })),
    title: 'Posts',
    titleKey: 'website.routes.posts',
    icon: 'globe',
    active: false,
    noPagePadding: true,
    parent: websiteSingle,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/details`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
};

const postsByType: RouteConfig = {
  path: '/websites/:websiteId/posts/:postType',
  name: 'PostDetails',
  component: () => import('@/views/websites/single/posts/single/Index.vue'),
  meta: {
    auth: {
      required: true,
      permissions: postTypes.map((post) => ({
        keyType: 'site',
        key: post.value.toLowerCase(),
        accessType: 'read',
        checkAtLeastOneModel: true,
      })) as IRouteMetaPermissionConfig[],
      condition: 'or',
    },
    hidePageTitle: true,
    noPagePadding: true,
    title: 'Post Details',
    titleKey: 'website.routes.postDetails',
    icon: 'globe',
    active: false,
    parent: posts,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/details`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        title: vm?.$ts('website.routes.posts') ?? 'Posts',
        to: `/websites/${vm?.$route.params.websiteId}/posts`,
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
    backButtonTarget: () => ({ target: posts, isCustom: false }),
  },
};

const cityPages: RouteConfig = {
  path: '/websites/:websiteId/citypages',
  name: 'CityPages',
  component: () => import('@/views/websites/single/pages/CityPages.vue'),
  meta: {
    auth: {
      required: true,
      permissions: [{ keyType: 'site', key: 'cities', accessType: 'read', idParam: 'websiteId' }],
    },
    title: 'City Pages',
    titleKey: 'website.routes.cityPages',
    icon: 'city',
    hidePageTitle: true,
    noPagePadding: true,
    parent: websiteSingle,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
};

const menu: RouteConfig = {
  path: '/websites/:websiteId/menu',
  name: 'Menu',
  component: () => import('@/views/websites/single/menu/Index.vue'),
  meta: {
    auth: {
      ...auth,
      permissions: [{ keyType: 'site', key: 'menu_editor', accessType: 'read', idParam: 'websiteId' }],
    },
    hidePageTitle: true,
    noPagePadding: true,
    title: 'Menu',
    titleKey: 'website.routes.menu',
    parent: websiteSingle,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
};

const formSubmissions: RouteConfig = {
  path: '/websites/:websiteId/form-submissions',
  name: 'FormSubmissions',
  component: () => import(/* webpackChunkName: "group-websites" */ '@/views/websites/single/form-submissions/Index.vue'),
  meta: {
    auth: {
      ...auth,
      permissions: [{ keyType: 'site', key: 'inquiries', accessType: 'read', idParam: 'websiteId' }],
    },
    icon: 'mailbox',
    title: 'Submissions',
    titleKey: 'website.routes.submissions',
    hidePageTitle: false,
    noPagePadding: true,
    parent: websiteSingle,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/form-submissions`,
        title: 'Forms',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
};

const inquiriesList: RouteConfig = {
  path: '/websites/:websiteId/inquiries/detailview',
  name: 'InquiriesList',
  component: () => import('@/views/websites/single/inquiries/DetailInquiries.vue'),
  meta: {
    auth: {
      ...auth,
      permissions: [{ keyType: 'site', key: 'inquiries', accessType: 'read', idParam: 'websiteId' }],
    },
    icon: 'mailbox',
    title: 'Inquiry Details',
    titleKey: 'website.routes.inquiryDetails',
    hidePageTitle: true,
    noPagePadding: true,
    hideFromNav: true,
    parent: formSubmissions,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/form-submissions`,
        title: 'Forms',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/form-submissions`,
        title: 'Submissions',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
    backButtonTarget: () => ({ isCustom: false, target: formSubmissions }),
  },
};

const customFormsAlt: RouteConfig = {
  path: '/websites/:websiteId/forms/responses',
  name: 'CustomFormSubmissionsAlt',
  component: () => import(/* webpackChunkName: "group-websites" */ '@/views/websites/single/inquiries/CustomFormsInbox.vue'),
  meta: {
    auth: {
      ...auth,
      permissions: [{ keyType: 'site', key: 'inquiries', accessType: 'read', idParam: 'websiteId' }],
    },
    icon: 'mailbox',
    title: 'Custom Forms',
    titleKey: 'website.routes.customForms',
    noPagePadding: true,
    hideFromNav: true,
  },
};

const customFormsList: RouteConfig = {
  path: '/websites/:websiteId/custom-forms-inbox/detailview/:formName',
  name: 'customFormsList',
  component: () => import('@/views/websites/single/inquiries/CustomFormsDetailView.vue'),
  meta: {
    auth: {
      ...auth,
      permissions: [{ keyType: 'site', key: 'inquiries', accessType: 'read', idParam: 'websiteId' }],
    },
    icon: 'mailbox',
    title: 'Submission Details',
    titleKey: 'website.routes.submissionDetails',
    hidePageTitle: true,
    noPagePadding: true,
    hideFromNav: true,
    parent: formSubmissions,
    backButtonTarget: () => ({ isCustom: false, target: formSubmissions }),
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/form-submissions`,
        title: 'Forms',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/form-submissions`,
        title: 'Submissions',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
};

const customFormsListAlt: RouteConfig = {
  path: '/websites/:websiteId/forms/responses/detailview/:formName',
  name: 'CustomFormsListAlt',
  component: () => import(/* webpackChunkName: "group-websites" */ '@/views/websites/single/inquiries/CustomFormsDetailView.vue'),
  meta: {
    auth: {
      ...auth,
      permissions: [{ keyType: 'site', key: 'inquiries', accessType: 'read', idParam: 'websiteId' }],
    },
    icon: 'mailbox',
    title: 'Custom Forms Inbox',
    titleKey: 'website.routes.customFormsInbox',
    hidePageTitle: true,
    noPagePadding: true,
    hideFromNav: true,
    parent: customFormsAlt,
    backButtonTarget: () => ({ isCustom: false, target: customFormsAlt }),
  },
};

const customFields: RouteConfig = {
  path: '/websites/:websiteId/global-content',
  name: 'WebsiteCustomFields',
  component: () => import('@/views/websites/single/global-content/Index.vue'),
  meta: {
    auth: siteSettingsAuth,
    icon: 'toolbox',
    title: 'Global Content',
    titleKey: 'website.routes.globalContent',
    parent: websiteSingle,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
};

const settings: RouteConfig = {
  path: '/websites/:websiteId/settings',
  name: 'WebsiteSettings',
  redirect: () => {
    try {
      return app()?.$rsGlobals.$userHasAccess({ keyType: 'site', key: 'site_management', accessType: 'read', idParam: 'websiteId' })
        ? '/websites/:websiteId/settings/global'
        : '/websites/:websiteId/settings/forms';
    } catch (e) {
      console.warn(e);
      return '/websites/:websiteId/settings/global';
    }
  },
  component: () => import('@/views/websites/single/settings/Index.vue'),
  meta: {
    auth,
    dynamicHideFromNav: () => {
      if (app()?.$rsGlobals.$userHasAccess({
        keyType: ModelType.site,
        key: 'pages',
        accessType: 'update',
        idParam: 'websiteId',
      })) {
        return false;
      }

      if (app()?.$rsGlobals.$userHasAccess({
        keyType: ModelType.site,
        key: 'form_settings',
        accessType: 'read',
        idParam: 'websiteId',
      })) {
        return false;
      }

      return true;
    },
    noPagePadding: true,
    title: 'Settings',
    titleKey: 'website.routes.settings',
    icon: 'cogs',
    active: false,
    hideFromNav: false,
    parent: websiteSingle,
  },
};

const websiteGlobalSettings: RouteConfig = {
  name: 'WebsiteGlobalSettings',
  path: '/websites/:websiteId/settings/global',
  meta: {
    auth: siteSettingsAuth,
    title: 'Website Settings',
    titleKey: 'website.routes.websiteSettings',
    icon: 'cogs',
    active: false,
    parent: settings,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
    footerBar: {
      enabled: true,
      // Permission gating handled inside component; static true avoids app() circular init
      hasSave: true,
      hasBottomText: true,
      hasTranslationsBtn: () => !(app()?.$rsGlobals.$siteSourceClientLanguages?.length),
    },
  },
  component: () => import('@/views/websites/single/settings/Global.vue'),
};

const websiteFormSettings: RouteConfig = {
  name: 'WebsiteFormSettings',
  path: '/websites/:websiteId/settings/forms',
  meta: {
    auth: {
      required: true,
      permissions: [{ keyType: 'site', key: 'form_settings', accessType: 'read', idParam: 'websiteId' }],
    },
    title: 'Form Settings',
    titleKey: 'website.routes.formSettings',
    icon: 'cogs',
    active: false,
    parent: settings,
    noPagePadding: true,
  },
  component: () => import('@/views/websites/single/settings/forms/Index.vue'),
};

const websiteFormSettingsAlt: RouteConfig = {
  name: 'WebsiteFormSettingsAlt',
  path: '/websites/:websiteId/forms/settings',
  meta: {
    auth: {
      required: true,
      permissions: [{ keyType: 'site', key: 'form_settings', accessType: 'read', idParam: 'websiteId' }],
    },
    title: 'Settings',
    titleKey: 'website.routes.settings',
    icon: 'cogs',
    active: false,
    parent: settings,
    noPagePadding: true,
    hideFromNav: true,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/form-submissions`,
        title: 'Forms',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
  component: () => import(/* webpackChunkName: "group-website-settings-forms" */ '@/views/websites/single/settings/forms/Index.vue'),
};

const websiteFormSettingsSingle: RouteConfig = {
  name: 'FormDetails',
  path: '/websites/:websiteId/settings/forms/:id/details',
  meta: {
    auth: {
      required: true,
      permissions: [{ keyType: 'site', key: 'form_settings', accessType: 'read', idParam: 'websiteId' }],
    },
    title: 'Form Details',
    titleKey: 'website.routes.formDetails',
    icon: 'cogs',
    active: false,
    noPagePadding: true,
    hidePageTitle: true,
    parent: websiteFormSettings,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasDelete: true,
    },
  },
  component: () => import('@/views/websites/single/settings/forms/single/Index.vue'),
};

const websiteFormSettingsSingleAlt: RouteConfig = {
  name: 'WebsiteFormDetailsAlt',
  path: '/websites/:websiteId/forms/settings/:id/details',
  meta: {
    auth: {
      required: true,
      permissions: [{ keyType: 'site', key: 'form_settings', accessType: 'read', idParam: 'websiteId' }],
    },
    title: 'Details',
    titleKey: 'website.routes.details',
    icon: 'cogs',
    active: false,
    noPagePadding: true,
    hidePageTitle: true,
    parent: websiteFormSettingsAlt,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasDelete: true,
    },
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/form-submissions`,
        title: 'Forms',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/forms/settings`,
        title: 'Settings',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
  component: () => import(/* webpackChunkName: "group-website-settings-forms" */ '@/views/websites/single/settings/forms/single/Index.vue'),
};

const adminSettings: RouteConfig = {
  path: '/websites/:websiteId/superadmin-settings',
  redirect: '/websites/:websiteId/superadmin-settings/global',
  name: 'WebsiteAdminSettings',
  component: () => import('@/views/websites/single/superadmin-settings/Index.vue'),
  meta: {
    auth: superadminAuth,
    noPagePadding: true,
    title: 'Superadmin Settings',
    titleKey: 'website.routes.superadminSettings',
    icon: 'cogs',
    active: false,
    hideFromNav: false,
    parent: websiteSingle,
  },
};

const globalSettings: RouteConfig = {
  name: 'GlobalSettings',
  path: '/websites/:websiteId/superadmin-settings/global',
  meta: {
    auth: superadminAuth,
    title: 'Global Settings',
    titleKey: 'website.routes.globalSettings',
    icon: 'cogs',
    active: false,
    parent: adminSettings,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/superadmin-settings/global`,
        title: 'Superadmin Settings',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
  component: () => import('@/views/websites/single/superadmin-settings/Global.vue'),
};

const languageSettings: RouteConfig = {
  name: 'LanguageSettings',
  path: '/websites/:websiteId/superadmin-settings/language',
  meta: {
    auth: superadminAuth,
    title: 'Language Settings',
    titleKey: 'website.routes.languageSettings',
    icon: 'cogs',
    active: false,
    parent: adminSettings,
    dynamicHideFromNav: () => !!app()?.$rsGlobals.$currentClient.parentId,
    footerBar: {
      enabled: true,
      hasSave: true,
    },
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/superadmin-settings/global`,
        title: 'Superadmin Settings',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
  component: () => import('@/views/websites/single/superadmin-settings/Language.vue'),
};

const apiKeySettings: RouteConfig = {
  name: 'APIKeySettings',
  path: '/websites/:websiteId/superadmin-settings/api-keys',
  meta: {
    auth: superadminAuth,
    title: 'APIs & Keys',
    titleKey: 'website.routes.apiKeys',
    icon: 'cogs',
    active: false,
    parent: adminSettings,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/superadmin-settings/global`,
        title: 'Superadmin Settings',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
  component: () => import('@/views/websites/single/superadmin-settings/ApiKey.vue'),
};

const footerScriptsSettings: RouteConfig = {
  name: 'FooterScripts',
  path: '/websites/:websiteId/superadmin-settings/footer-scripts',
  meta: {
    auth: superadminAuth,
    title: 'Footer Scripts',
    titleKey: 'website.routes.footerScripts',
    icon: 'cogs',
    active: false,
    parent: adminSettings,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/superadmin-settings/global`,
        title: 'Superadmin Settings',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
  component: () => import('@/views/websites/single/superadmin-settings/FooterScripts.vue'),
};

const tagScripts: RouteConfig = {
  name: 'TagScripts',
  path: '/websites/:websiteId/superadmin-settings/tag-scripts',
  meta: {
    auth: superadminAuth,
    title: 'Tag Scripts',
    titleKey: 'website.routes.tagScripts',
    icon: 'cogs',
    active: false,
    parent: adminSettings,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/superadmin-settings/global`,
        title: 'Superadmin Settings',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
    footerBar: {
      enabled: true,
      hasSave: true,
    },
  },

  component: () => import('@/views/websites/single/superadmin-settings/TagScripts.vue'),
};

const panelSettings: RouteConfig = {
  name: 'WebsitePanelTemplates',
  component: () => import('@/views/settings/panel-templates/Index.vue'),
  path: '/websites/:websiteId/superadmin-settings/panels',
  meta: {
    auth: superadminAuth,
    title: 'Panel Templates',
    titleKey: 'website.routes.panelTemplates',
    icon: 'cogs',
    active: false,
    noPagePadding: true,
    parent: adminSettings,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/pages`,
        title: vm?.$rsGlobals.$sidebarHeaderData.title ?? '',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/superadmin-settings/global`,
        title: 'Superadmin Settings',
      },
      {
        title: vm?.$route.meta?.title ?? '',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
};

const panelTemplateSingle: RouteConfig = {
  path: '/websites/:websiteId/superadmin-settings/panels/:id/details',
  name: 'WebsitePanelTemplateSingle',
  component: () => import('@/views/settings/panel-templates/single/Index.vue'),
  meta: {
    auth: superadminAuth,
    title: 'Edit Panel Template',
    noPagePadding: true,
    icon: 'cogs',
    active: false,
    parent: panelSettings,
    breadcrumbs: (vm) => ([
      {
        to: '/websites',
        title: 'Websites',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/superadmin-settings/global`,
        title: 'Superadmin Settings',
      },
      {
        to: `/websites/${vm?.$route.params.websiteId}/superadmin-settings/panels`,
        title: 'Panel Templates',
      },
      {
        title: 'Details',
        to: vm?.$route.path ?? '',
      },
    ]),
  },
};

// Export the routes to be loaded in index.ts
export default [
  websites,
  websiteSingle,
  menu,
  pages,
  posts,
  postsByType,
  cityPages,
  customFields,
  formSubmissions,
  customFormsList,
  settings,
  adminSettings,
  inquiriesList,
  websiteFormSettingsSingle,
  websiteGlobalSettings,
  websiteFormSettings,
  globalSettings,
  languageSettings,
  apiKeySettings,
  tagScripts,
  footerScriptsSettings,
  panelSettings,
  panelTemplateSingle,
  websiteFormSettingsAlt,
  websiteFormSettingsSingleAlt,
  customFormsAlt,
  customFormsListAlt,
] as Array<RouteConfig>;
